import { type FC, useMemo } from "react";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { useLanguage } from "src/hooks/useLanguage";
import type { SalesAttributionSeriesKey, SalesAttributionTrendPoint } from "../utils/salesAttributionTypes";
import {
  AXIS_TICK,
  CHART_CHROME,
  LEGEND_PROPS,
  SOURCE_COLORS,
  localizeChartDigits,
  mirrorChartMargin,
  rtlChartProps,
  shadeColor,
} from "./salesAttributionShared";

export type SalesAttributionMetricType = "orders" | "revenue" | "spend";
const TREND_CHART_MARGIN = { top: 8, right: 8, bottom: 0, left: -8 };

const compactTick = (value: number, isRTL: boolean): string => {
  if (!Number.isFinite(value)) {
    return "";
  }
  const abs = Math.abs(value);
  const sign = value < 0 ? "-" : "";
  if (abs >= 1_000_000) {
    return localizeChartDigits(`${sign}${(abs / 1_000_000).toFixed(1)}M`, isRTL);
  }
  if (abs >= 1_000) {
    return localizeChartDigits(`${sign}${(abs / 1_000).toFixed(1)}K`, isRTL);
  }
  return localizeChartDigits(`${sign}${Math.round(abs)}`, isRTL);
};

export type SalesAttributionSeriesPlatform = "google_ads" | "meta_ads";

interface SeriesConfig {
  key: SalesAttributionSeriesKey;
  name: string;
  chipLabel: string;
  metric: SalesAttributionMetricType;
  color: string;
  descriptionDefault: string;
  platform?: SalesAttributionSeriesPlatform;
}

export const RECONCILIATION_SERIES: SeriesConfig[] = [
  {
    key: "shopifyOrders",
    name: "Zid Orders",
    chipLabel: "Zid",
    metric: "orders",
    color: SOURCE_COLORS.shopify,
    descriptionDefault: "Zid total orders in Zid dataset",
  },
  {
    key: "ga4Orders",
    name: "GA4 Orders",
    chipLabel: "GA4",
    metric: "orders",
    color: SOURCE_COLORS.ga4,
    descriptionDefault: "GA4 total orders in GA4 dataset",
  },
  {
    key: "googleOrdersShopify",
    name: "Google Ads Orders (Zid)",
    chipLabel: "Google Ads (Zid)",
    metric: "orders",
    color: shadeColor(SOURCE_COLORS.googleAds, -0.15),
    descriptionDefault: "Google Ads total orders in Zid dataset",
    platform: "google_ads",
  },
  {
    key: "metaOrdersShopify",
    name: "Meta Ads Orders (Zid)",
    chipLabel: "Meta Ads (Zid)",
    metric: "orders",
    color: shadeColor(SOURCE_COLORS.metaAds, -0.15),
    descriptionDefault: "Meta Ads total orders in Zid dataset",
    platform: "meta_ads",
  },
  {
    key: "googleOrdersGA4",
    name: "Google Ads Orders (GA4)",
    chipLabel: "Google Ads (GA4)",
    metric: "orders",
    color: shadeColor(SOURCE_COLORS.googleAds, 0.3),
    descriptionDefault: "Google Ads total orders in GA4 dataset",
    platform: "google_ads",
  },
  {
    key: "metaOrdersGA4",
    name: "Meta Ads Orders (GA4)",
    chipLabel: "Meta Ads (GA4)",
    metric: "orders",
    color: shadeColor(SOURCE_COLORS.metaAds, 0.3),
    descriptionDefault: "Meta Ads total orders in GA4 dataset",
    platform: "meta_ads",
  },
  {
    key: "googleOrders",
    name: "Google Ads Orders",
    chipLabel: "Google Ads",
    metric: "orders",
    color: SOURCE_COLORS.googleAds,
    descriptionDefault: "Google Ads total orders in Platform dataset",
    platform: "google_ads",
  },
  {
    key: "metaOrders",
    name: "Meta Ads Orders",
    chipLabel: "Meta Ads",
    metric: "orders",
    color: SOURCE_COLORS.metaAds,
    descriptionDefault: "Meta Ads total orders in Platform dataset",
    platform: "meta_ads",
  },
  {
    key: "shopifyRevenue",
    name: "Zid Revenue",
    chipLabel: "Zid",
    metric: "revenue",
    color: shadeColor(SOURCE_COLORS.shopify, -0.15),
    descriptionDefault: "Zid total revenue in Zid dataset",
  },
  {
    key: "ga4Revenue",
    name: "GA4 Revenue",
    chipLabel: "GA4",
    metric: "revenue",
    color: shadeColor(SOURCE_COLORS.ga4, -0.15),
    descriptionDefault: "GA4 total revenue in GA4 dataset",
  },
  {
    key: "googleRevenue",
    name: "Google Ads Revenue",
    chipLabel: "Google Ads",
    metric: "revenue",
    color: shadeColor(SOURCE_COLORS.googleAds, -0.15),
    descriptionDefault: "Google Ads total revenue in Platform dataset",
    platform: "google_ads",
  },
  {
    key: "metaRevenue",
    name: "Meta Ads Revenue",
    chipLabel: "Meta Ads",
    metric: "revenue",
    color: shadeColor(SOURCE_COLORS.metaAds, -0.15),
    descriptionDefault: "Meta Ads total revenue in Platform dataset",
    platform: "meta_ads",
  },
  {
    key: "googleSpend",
    name: "Google Ads Spend",
    chipLabel: "Google Ads",
    metric: "spend",
    color: shadeColor(SOURCE_COLORS.googleAds, 0.3),
    descriptionDefault: "Google Ads total spend in Platform dataset",
    platform: "google_ads",
  },
  {
    key: "metaSpend",
    name: "Meta Ads Spend",
    chipLabel: "Meta Ads",
    metric: "spend",
    color: shadeColor(SOURCE_COLORS.metaAds, 0.3),
    descriptionDefault: "Meta Ads total spend in Platform dataset",
    platform: "meta_ads",
  },
];

export const SERIES_BY_KEY = new Map(RECONCILIATION_SERIES.map(series => [series.key, series]));

export const seriesMatchesPlatformFilter = (
  key: SalesAttributionSeriesKey,
  platformFilter: string
): boolean => {
  const series = SERIES_BY_KEY.get(key);
  if (!series?.platform) {
    return true;
  }
  if (!platformFilter || platformFilter === "all") {
    return true;
  }
  return series.platform === platformFilter;
};

export const seriesDisplayName = (t: TFunction, key: SalesAttributionSeriesKey): string =>
  t(`salesAttribution.ui.seriesNames.${key}`, {
    defaultValue: SERIES_BY_KEY.get(key)?.name ?? key,
  });

export const seriesChipLabel = (t: TFunction, key: SalesAttributionSeriesKey): string =>
  t(`salesAttribution.ui.seriesChipLabels.${key}`, {
    defaultValue: SERIES_BY_KEY.get(key)?.chipLabel ?? key,
  });

export const seriesGroupLabel = (t: TFunction, metric: SalesAttributionMetricType): string =>
  t(`salesAttribution.ui.seriesGroups.${metric}`, {
    defaultValue: metric === "orders" ? "Orders" : metric === "revenue" ? "Revenue" : "Spend",
  });

export const RECONCILIATION_SERIES_GROUPS: Array<{
  label: string;
  metric: SalesAttributionMetricType;
  keys: SalesAttributionSeriesKey[];
}> = [
  {
    label: "Orders",
    metric: "orders",
    keys: [
      "shopifyOrders",
      "ga4Orders",
      "googleOrdersShopify",
      "metaOrdersShopify",
      "googleOrdersGA4",
      "metaOrdersGA4",
      "googleOrders",
      "metaOrders",
    ],
  },
  {
    label: "Revenue",
    metric: "revenue",
    keys: ["shopifyRevenue", "ga4Revenue", "googleRevenue", "metaRevenue"],
  },
  { label: "Spend", metric: "spend", keys: ["googleSpend", "metaSpend"] },
];

interface ReconciliationTooltipProps {
  active?: boolean;
  payload?: Array<{
    dataKey?: string | number;
    name?: string;
    color?: string;
    payload?: SalesAttributionTrendPoint;
  }>;
}

const TooltipSeriesDot: FC<{ color: string }> = ({ color }) => (
  <svg width={8} height={8} viewBox="0 0 8 8" aria-hidden="true">
    <circle cx={4} cy={4} r={4} fill={color} />
  </svg>
);


const ReconciliationTooltip: FC<ReconciliationTooltipProps> = ({ active, payload }) => {
  const { t } = useTranslation();

  if (!active || !payload?.length) {
    return null;
  }

  const point = payload[0]?.payload;

  return (

    <div className="sales-attribution__chart-tooltip sales-attribution__chart-tooltip--wide">
      <p className="sales-attribution__chart-tooltip-title">{point?.dateLabel ?? ""}</p>
      <ul className="sales-attribution__chart-tooltip-list sales-attribution__chart-tooltip-list--grid">
        {payload.map(entry => {
          const key = entry.dataKey as SalesAttributionSeriesKey;
          const series = SERIES_BY_KEY.get(key);
          const seriesColor = entry.color ?? series?.color ?? SOURCE_COLORS.shopify;

          return (
            <li key={String(entry.dataKey)} className="sales-attribution__chart-tooltip-row">
              <span className="sales-attribution__chart-tooltip-label sales-attribution__chart-tooltip-label--with-dot">
                <TooltipSeriesDot color={seriesColor} />
                <span className="sales-attribution__chart-tooltip-label-text">
                  {series ? seriesDisplayName(t, key) : entry.name ?? key}
                </span>
              </span>
              <span className="sales-attribution__chart-tooltip-value">
                {point?.formatted?.[key] ?? "—"}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

interface ReconciliationChartProps {
  data: SalesAttributionTrendPoint[];
  activeSeries: SalesAttributionSeriesKey[];
}

export const ReconciliationChart: FC<ReconciliationChartProps> = ({
  data,
  activeSeries,
}) => {
  const { t } = useTranslation();
  const { isRTL } = useLanguage();
  const chartRtl = rtlChartProps(isRTL);
  const active = useMemo(
    () =>
      RECONCILIATION_SERIES.filter(series => activeSeries.includes(series.key)),
    [activeSeries]
  );

  const hasOrders = active.some(series => series.metric === "orders");
  const hasValue = active.some(series => series.metric !== "orders");

  return (
    <div
      className="sales-attribution__chart sales-attribution__chart--reconciliation"
      role="img"
      aria-label={t("salesAttribution.ui.reconciliationChartAria", {
        defaultValue: "Reconciliation chart",
      })}
    >
      <ResponsiveContainer width="100%" height={340}>
        <ComposedChart data={data} margin={mirrorChartMargin(TREND_CHART_MARGIN, isRTL)}>
          <CartesianGrid stroke={CHART_CHROME.grid} vertical={false} />
          <XAxis
            dataKey="dateLabel"
            reversed={chartRtl.axisReversed}
            tick={AXIS_TICK}
            tickLine={false}
            axisLine={{ stroke: CHART_CHROME.axis }}
            minTickGap={16}
          />

          <YAxis
            yAxisId="orders"
            orientation={chartRtl.yAxisOrientation}
            hide={!hasOrders}
            tickFormatter={value => compactTick(Number(value), isRTL)}
            tick={AXIS_TICK}
            tickLine={false}
            axisLine={false}
            width={56}
          />
          <YAxis
            yAxisId="value"
            orientation={chartRtl.oppositeYAxisOrientation}
            hide={!hasValue}
            tickFormatter={value => compactTick(Number(value), isRTL)}
            tick={AXIS_TICK}
            tickLine={false}
            axisLine={false}
            width={82}
          />
          <Tooltip
            cursor={{ fill: CHART_CHROME.cursor }}
            content={<ReconciliationTooltip />}
          />
          <Legend {...LEGEND_PROPS} />

          {active
            .filter(series => series.metric === "orders")
            .map(series => (
              <Bar
                key={series.key}
                yAxisId="orders"
                dataKey={series.key}
                name={seriesDisplayName(t, series.key)}
                fill={series.color}
                fillOpacity={0.85}
                radius={[3, 3, 0, 0]}
                maxBarSize={22}
              />
            ))}

          {active
            .filter(series => series.metric !== "orders")
            .map(series => (
              <Line
                key={series.key}
                yAxisId="value"
                type="monotone"
                dataKey={series.key}
                name={seriesDisplayName(t, series.key)}
                stroke={series.color}
                strokeWidth={2}
                dot={false}
                strokeDasharray={series.metric === "spend" ? "5 3" : undefined}
              />
            ))}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};

export default ReconciliationChart;
